// File: src/components/profile/OfflinePlaybackCard.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getFirestore, doc, onSnapshot, collection, getDocs } from "firebase/firestore";
import { motion } from "framer-motion";
import { useProfile } from "../../context/ProfileContext";
import { useMusic } from "../../context/MusicContext";
import { usePlaylistArtwork } from "../../hooks/usePlaylistArtwork";

const OfflinePlaybackCard = () => {
  const navigate = useNavigate();
  const { activeProfile, isDarkMode } = useProfile();
  
  // 🌟 Check all common property names from MusicContext (playSource, queueSource, source)
  const musicContext = useMusic();
  const isPlaying = musicContext?.isPlaying;
  const currentSource = (
    musicContext?.playSource || 
    musicContext?.queueSource || 
    musicContext?.source || 
    ""
  ).toLowerCase().trim();

  const [offlineDoc, setOfflineDoc] = useState({ songIds: [] });
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    if (!activeProfile?.id) {
      setIsInitializing(false);
      return;
    }

    const db = getFirestore();
    const docRef = doc(db, "users", activeProfile.id, "specialPlaylists", "offlinePlayback");

    const unsubscribe = onSnapshot(docRef, async (snap) => {
      if (snap.exists()) {
        setOfflineDoc(snap.data());
        setIsInitializing(false);
      } else {
        try {
          const legacyRef = collection(db, "users", activeProfile.id, "downloadableSongs");
          const legacySnap = await getDocs(legacyRef);
          const legacyIds = legacySnap.docs.map((d) => d.id);
          setOfflineDoc({ songIds: legacyIds });
        } catch (err) {
          console.error("Error reading legacy offline songs:", err);
        } finally {
          setIsInitializing(false);
        }
      }
    });

    return () => unsubscribe();
  }, [activeProfile?.id]);

  const { artworkUrls, isLoading: isArtworkLoading } = usePlaylistArtwork(offlineDoc);
  const songCount = offlineDoc.songIds?.length || 0;

  // Evaluates to true whether the source string is "Offline Playback" or "OFFLINE PLAYBACK"
  const isPlayingFromThisCard = Boolean(isPlaying && currentSource === "offline playback");

  if (isInitializing) {
    return (
      <div className="w-full h-14 sm:h-16 rounded-xl animate-pulse bg-slate-200 dark:bg-zinc-800" />
    );
  }

  return (
    <div
      onClick={() => navigate("/offline-playback")}
      className={`group flex items-center h-14 sm:h-16 rounded-xl overflow-hidden cursor-pointer select-none transition-all duration-200 relative border w-full ${
        isDarkMode
          ? "bg-zinc-900/80 hover:bg-zinc-800/90 border-zinc-800/80 text-white"
          : "bg-slate-100 hover:bg-slate-200/80 border-slate-200/80 text-slate-900"
      }`}
    >
      {/* Left Artwork Thumbnail (2x2 grid if 4 available, else single) */}
      <div className="h-14 w-14 sm:h-16 sm:w-16 flex-shrink-0 relative overflow-hidden bg-zinc-800">
        {isArtworkLoading ? (
          <div className="w-full h-full bg-zinc-700 animate-pulse" />
        ) : artworkUrls.length === 4 ? (
          <div className="grid grid-cols-2 grid-rows-2 h-full w-full">
            {artworkUrls.map((url, i) => (
              <img
                key={i}
                src={url}
                alt="Cover"
                className="w-full h-full object-cover"
              />
            ))}
          </div>
        ) : (
          <img
            src={artworkUrls[0] || "/default-artwork.png"}
            alt="Offline Playback Cover"
            className="w-full h-full object-cover"
          />
        )}
      </div>

      {/* 🌟 Spotify-Style Text Area (Wrapped up to 2 lines without truncate cutoff) */}
      <div className="flex-1 min-w-0 px-2 sm:px-3 py-1 flex flex-col justify-center">
        <p className="font-bold text-[11px] sm:text-xs leading-[1.2] line-clamp-2 break-words">
          Offline Playback
        </p>
        {songCount > 0 && (
          <p className="text-[9px] sm:text-[10px] font-semibold text-slate-500 dark:text-zinc-400 truncate mt-0.5">
            {songCount} {songCount === 1 ? "Track" : "Tracks"}
          </p>
        )}
      </div>

      {/* Right Indicator: Animated Waveform or Pink Play Button */}
      <div className="pr-2 sm:pr-3 flex-shrink-0 flex items-center justify-center">
        {isPlayingFromThisCard ? (
          /* 🌟 Framer Motion Equalizer Waveform */
          <div className="flex items-end justify-center gap-[3px] h-4 w-5 px-0.5">
            <motion.span
              animate={{ height: ["25%", "95%", "35%"] }}
              transition={{ repeat: Infinity, duration: 0.7, ease: "easeInOut" }}
              className="w-[3px] bg-[#ff00cc] rounded-full inline-block"
            />
            <motion.span
              animate={{ height: ["60%", "25%", "100%"] }}
              transition={{ repeat: Infinity, duration: 0.85, ease: "easeInOut", delay: 0.15 }}
              className="w-[3px] bg-[#ff00cc] rounded-full inline-block"
            />
            <motion.span
              animate={{ height: ["90%", "30%", "70%"] }}
              transition={{ repeat: Infinity, duration: 0.65, ease: "easeInOut", delay: 0.3 }}
              className="w-[3px] bg-[#ff00cc] rounded-full inline-block"
            />
          </div>
        ) : (
          /* Pink Play Button (Desktop Hover) */
          <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 bg-[#ff00cc] text-white opacity-0 group-hover:opacity-100 scale-90 group-hover:scale-100 hover:brightness-110">
            <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfflinePlaybackCard;